import { COMMENTARY_FILTERS } from "~/store/mystore/notes.redux";
import { useTypedSelector } from "./useTypedSelector";

// Map of RTK data
const FILTER_QUERY_MAP: Partial<Record<COMMENTARY_FILTERS, string[]>> = {
  [COMMENTARY_FILTERS.OVERVIEW]: [
    "salesPerformance",
    "fetchPerformanceAgainstForecastData",
    "trafficAndConversionTrend",
    "salesByCategory",
    "salesByProduct",
  ],
  [COMMENTARY_FILTERS.CUSTOMER]: [
    "trafficAndConversionSummary",
    "trafficAndConversionProductTrend",
    "cohortTrend",
    "cohortSummary",
    "repeatPurchaseSummary",
    "repeatPurchaseByProduct",
    "subscribeAndSaveSummary",
    "subscribeAndProductPerformance",
    "refundAnalyticsSummary",
    "refundAnalyticsProducts",
  ],
  [COMMENTARY_FILTERS.SALES]: [
    "salesByCategory",
    "salesByCountry",
    "customGroupMetrics",
    "salesByPeriod",
  ],
  [COMMENTARY_FILTERS.MARKETING]: [
    "marketingAllCampaignsChart",
    "marketingAllCampaignsTotals",
    "marketingCampaigns",
    "salesPerformance",
  ],
  [COMMENTARY_FILTERS.OPERATIONS]: [
    "inventoryReplenishmentSettings",
    "inventoryStatus",
  ],
  [COMMENTARY_FILTERS.PROFITABILITY]: [
    "fetchProductProfitability",
    "profitabilityChart",
  ],
};

// Map of non RTK data for specific filters
const FILTER_EXTRA_CONTEXT: Partial<
  Record<COMMENTARY_FILTERS, { name: string; selector: (state: any) => any }[]>
> = {
  [COMMENTARY_FILTERS.OVERVIEW]: [
    { name: "Profit and loss", selector: (s) => s.profitability.profitAndLoss },
    {
      name: "Suppressed product summary",
      selector: (s) => s.suppressedProducts,
    },
    { name: "Account health", selector: (s) => s.mystore.accountHealth },
    { name: "Sales by brand ", selector: (s) => s.mystore.salesByBrand },
  ],
  [COMMENTARY_FILTERS.SALES]: [
    { name: "salesByBrand", selector: (s) => s.mystore.salesByBrand },
    { name: "parentProducts", selector: (s) => s.parentProducts },
  ],
  [COMMENTARY_FILTERS.MARKETING]: [
    { name: "Top performing keywords", selector: (s) => s.marketing.keywords },
    {
      name: "Top performing product ads",
      selector: (s) => s.marketing.productAds,
    },
    {
      name: "Advertising performance by campaign type",
      selector: (s) => s.marketing.campaignTypes,
    },
  ],
  [COMMENTARY_FILTERS.OPERATIONS]: [
    { name: "Account health", selector: (s) => s.mystore.accountHealth },
    { name: "Overdue order", selector: (s) => s.mystore.overdueOrdersList },
    {
      name: "Out of stock products",
      selector: (s) => s.mystore.outOfStockProducts,
    },
  ],
  [COMMENTARY_FILTERS.PROFITABILITY]: [
    {
      name: "Channel profit",
      selector: (s) => s.mystore?.profitability?.overview ?? "",
    },
  ],
  [COMMENTARY_FILTERS.OTHER]: [
    { name: "BuyBox summary", selector: (s) => s.buyBox.snapshot ?? "" },
    {
      name: "Buybox winning products/Buybox losing products",
      selector: (s) => s.buyBox.products ?? "",
    },
    { name: "suppressedProducts", selector: (s) => s.suppressedProducts },
  ],
};

// TODO: Fix this hook
// The is not the right way to get the data from RTK
export const useCommentaryContext = (filter: COMMENTARY_FILTERS) => {
  const cachedDataSet = useTypedSelector((state) => state.api.queries);
  const queryKeys = Object.keys(cachedDataSet);
  const rtkQueries = FILTER_QUERY_MAP[filter] || [];

  const context: { name: string; data: any }[] = [];

  rtkQueries.forEach((rtkQuery) => {
    const match = queryKeys.find((key) => key.includes(rtkQuery));
    if (match) {
      context.push({ name: rtkQuery, data: cachedDataSet[match]?.data ?? "" });
    }
  });

  // Add non-RTK context data
  const extraData = FILTER_EXTRA_CONTEXT[filter] || [];
  const fullState = useTypedSelector((state) => state);

  extraData.forEach(({ name, selector }) => {
    const data = selector(fullState);
    context.push({ name, data });
  });

  return context;
};
